// frontend/src/components/common/ui/PageFilters.tsx
// Unified filter component for consistent filtering across pages

import React, { useState } from 'react';
import { Filter, ChevronDown, X, Search } from 'lucide-react';

// Utility function to combine class names
const cn = (...classes: (string | undefined | null | boolean)[]): string => {
  return classes.filter(Boolean).join(' ');
};

export interface FilterOption {
  label: string;
  value: string | number;
  count?: number;
}

export interface FilterField {
  key: string;
  label: string;
  type: 'select' | 'multiselect' | 'search' | 'daterange' | 'toggle' | 'radio';
  options?: FilterOption[];
  placeholder?: string;
  value?: any;
  defaultValue?: any;
  searchable?: boolean;
  clearable?: boolean;
  width?: 'full' | 'auto' | 'sm' | 'md' | 'lg';
}

export interface PageFiltersProps {
  fields: FilterField[];
  values: Record<string, any>;
  onChange: (key: string, value: any) => void;
  onClear: () => void;
  isVisible?: boolean;
  onToggle?: () => void;
  hasActiveFilters?: boolean;
  variant?: 'default' | 'compact' | 'sidebar';
  className?: string;
  children?: React.ReactNode;
}

// Field width classes
const widthClasses = {
  full: 'w-full',
  auto: 'w-auto min-w-32',
  sm: 'w-32',
  md: 'w-48', 
  lg: 'w-64',
};

const PageFilters: React.FC<PageFiltersProps> = ({
  fields,
  values,
  onChange,
  onClear,
  isVisible = true,
  onToggle,
  hasActiveFilters = false,
  variant = 'default',
  className,
  children,
}) => {
  const [searchableFields, setSearchableFields] = useState<Record<string, string>>({});

  const handleFieldChange = (key: string, value: any) => {
    onChange(key, value);
  };

  const getFilteredOptions = (field: FilterField): FilterOption[] => {
    if (!field.options || !field.searchable) return field.options || [];
    
    const searchTerm = searchableFields[field.key]?.toLowerCase() || '';
    if (!searchTerm) return field.options;
    
    return field.options.filter(option =>
      option.label.toLowerCase().includes(searchTerm)
    );
  };

  const renderSearchField = (field: FilterField) => (
    <div className={cn('relative', widthClasses[field.width || 'md'])}>
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <Search className="h-4 w-4 text-gray-400" />
      </div>
      <input
        type="text"
        value={values[field.key] || ''}
        onChange={(e) => handleFieldChange(field.key, e.target.value)}
        placeholder={field.placeholder || `Search ${field.label.toLowerCase()}...`}
        className={cn(
          'block w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm',
          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
          'placeholder-gray-400'
        )}
      />
      {field.clearable && values[field.key] && (
        <button
          onClick={() => handleFieldChange(field.key, '')}
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
        >
          <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
        </button>
      )}
    </div>
  );

  const renderSelectField = (field: FilterField) => {
    const filteredOptions = getFilteredOptions(field);
    
    return (
      <div className={cn('relative', widthClasses[field.width || 'md'])}>
        {field.searchable && (
          <div className="mb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchableFields[field.key] || ''}
                onChange={(e) => setSearchableFields(prev => ({
                  ...prev,
                  [field.key]: e.target.value
                }))}
                placeholder={`Search ${field.label.toLowerCase()}...`}
                className="block w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        )}
        
        <select
          value={values[field.key] || ''}
          onChange={(e) => handleFieldChange(field.key, e.target.value)}
          className={cn(
            'block w-full px-3 py-2 border border-gray-300 rounded-md text-sm',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
            'bg-white'
          )}
        >
          <option value="">
            All {field.label}
          </option>
          {filteredOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
              {option.count !== undefined && ` (${option.count})`}
            </option>
          ))}
        </select>
        
        {field.clearable && values[field.key] && (
          <button
            onClick={() => handleFieldChange(field.key, '')}
            className="absolute right-8 top-1/2 transform -translate-y-1/2"
          >
            <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>
    );
  };

  const renderToggleField = (field: FilterField) => (
    <div className={cn('flex items-center', widthClasses[field.width || 'auto'])}>
      <label className="flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={values[field.key] || false}
          onChange={(e) => handleFieldChange(field.key, e.target.checked)}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <span className="ml-2 text-sm text-gray-700">{field.label}</span>
      </label>
    </div>
  );

  const renderRadioField = (field: FilterField) => (
    <div className={cn('space-y-2', widthClasses[field.width || 'md'])}>
      <div className="text-sm font-medium text-gray-700">{field.label}</div>
      <div className="space-y-1">
        {field.options?.map((option) => (
          <label key={option.value} className="flex items-center cursor-pointer">
            <input
              type="radio"
              name={field.key}
              value={option.value}
              checked={values[field.key] === option.value}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <span className="ml-2 text-sm text-gray-700">
              {option.label}
              {option.count !== undefined && (
                <span className="text-gray-500 ml-1">({option.count})</span>
              )}
            </span>
          </label>
        ))}
      </div>
    </div>
  );

  const renderMultiSelectField = (field: FilterField) => {
    const selectedValues = values[field.key] || [];
    const isSelected = (value: string | number) => selectedValues.includes(value);
    
    const toggleOption = (value: string | number) => {
      const newValues = isSelected(value)
        ? selectedValues.filter((v: any) => v !== value)
        : [...selectedValues, value];
      handleFieldChange(field.key, newValues);
    };

    return (
      <div className={cn('space-y-2', widthClasses[field.width || 'md'])}>
        <div className="text-sm font-medium text-gray-700">{field.label}</div>
        <div className="max-h-32 overflow-y-auto space-y-1 border border-gray-300 rounded-md p-2">
          {field.options?.map((option) => (
            <label key={option.value} className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isSelected(option.value)}
                onChange={() => toggleOption(option.value)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                {option.label}
                {option.count !== undefined && (
                  <span className="text-gray-500 ml-1">({option.count})</span>
                )}
              </span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const renderField = (field: FilterField) => {
    switch (field.type) {
      case 'search':
        return renderSearchField(field);
      case 'select':
        return renderSelectField(field);
      case 'toggle':
        return renderToggleField(field);
      case 'radio':
        return renderRadioField(field);
      case 'multiselect':
        return renderMultiSelectField(field);
      default:
        return null;
    }
  };

  // Count active filters
  const activeFilterCount = Object.values(values).filter(value => {
    if (Array.isArray(value)) return value.length > 0;
    return value !== '' && value !== undefined && value !== null && value !== false;
  }).length;

  if (variant === 'compact') {
    return (
      <div className={cn('bg-white border-b border-gray-200 px-4 py-3', className)}>
        <div className="flex items-center justify-between">
          <button
            onClick={onToggle}
            className={cn(
              'flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors',
              hasActiveFilters || activeFilterCount > 0
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            )}
          >
            <Filter className="h-4 w-4" />
            Filters
            {(activeFilterCount > 0) && (
              <span className="bg-blue-500 text-white text-xs rounded-full px-1.5 py-0.5 ml-1">
                {activeFilterCount}
              </span>
            )}
            <ChevronDown className={cn(
              'h-4 w-4 transition-transform duration-200',
              isVisible && 'transform rotate-180'
            )} />
          </button>
          
          {activeFilterCount > 0 && (
            <button
              onClick={onClear}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Clear all
            </button>
          )}
        </div>
        
        {isVisible && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {fields.map((field) => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label}
                </label>
                {renderField(field)}
              </div>
            ))}
            {children}
          </div>
        )}
      </div>
    );
  }

  if (variant === 'sidebar') {
    return (
      <div className={cn('bg-gray-50 p-4 space-y-6', className)}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Filters</h3>
          {activeFilterCount > 0 && (
            <button
              onClick={onClear}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear all ({activeFilterCount})
            </button>
          )}
        </div>
        
        <div className="space-y-6">
          {fields.map((field) => (
            <div key={field.key}>
              {renderField(field)}
            </div>
          ))}
          {children}
        </div>
      </div>
    );
  }

  // Default variant
  return (
    <div className={cn('bg-white border-b border-gray-200', className)}>
      <div className="px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Filters</h3>
          {activeFilterCount > 0 && (
            <button
              onClick={onClear}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear all ({activeFilterCount})
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {fields.map((field) => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
              </label>
              {renderField(field)}
            </div>
          ))}
          {children}
        </div>
      </div>
    </div>
  );
};

// Filter button for triggering filter panel
export const FilterButton: React.FC<{
  onClick: () => void;
  hasActiveFilters?: boolean;
  filterCount?: number;
  className?: string;
}> = ({ onClick, hasActiveFilters = false, filterCount = 0, className }) => (
  <button
    onClick={onClick}
    className={cn(
      'flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors',
      hasActiveFilters || filterCount > 0
        ? 'bg-blue-50 border-blue-200 text-blue-700'
        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50',
      className
    )}
  >
    <Filter className="h-4 w-4" />
    Filters
    {filterCount > 0 && (
      <span className="bg-blue-500 text-white text-xs rounded-full px-1.5 py-0.5 ml-1">
        {filterCount}
      </span>
    )}
  </button>
);

// Hook for managing filter state
export const useFilters = <T extends Record<string, any>>(
  initialFilters: T,
  onFiltersChange?: (filters: T) => void
) => {
  const [filters, setFilters] = useState<T>(initialFilters);
  
  const updateFilter = (key: string, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange?.(newFilters);
  };
  
  const clearFilters = () => {
    setFilters(initialFilters);
    onFiltersChange?.(initialFilters);
  };
  
  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    const initialValue = initialFilters[key as keyof T];
    if (Array.isArray(value)) return value.length > 0;
    return value !== initialValue && value !== '' && value !== undefined && value !== null;
  });
  
  return {
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    setFilters,
  };
};

export default PageFilters;